import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useCart } from '../contexts/CartContext'
import { configAPI } from '../services/api'
import './Cart.css'
import { useDocumentMeta } from '../seo/useDocumentMeta';

function Cart() {
  useDocumentMeta();
  const navigate = useNavigate()
  const { 
    cartItems, 
    updateQuantity, 
    removeFromCart, 
    getTotalItems, 
    getTotalPrice 
  } = useCart()

  // 兜底值保守取 Infinity：设 0 会让免运费判断恒成立，config 一挂就谎报免运费
  const [config, setConfig] = useState({
    freeShippingThreshold: Infinity,
    shippingCost: 10,
    taxRate: 0.1
  })
  const [subtotal, setSubtotal] = useState(0)
  const [shipping, setShipping] = useState(0)
  const [tax, setTax] = useState(0)
  const [total, setTotal] = useState(0)
  const [freeShippingProgress, setFreeShippingProgress] = useState(0)
  const [configLoading, setConfigLoading] = useState(true)

  // 从后端获取配置
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const result = await configAPI.getConfig()
        if (result.success && result.data) {
          setConfig({
            freeShippingThreshold: result.data.freeShippingThreshold,
            shippingCost: result.data.shippingCost,
            taxRate: result.data.taxRate
          })
        }
      } catch (error) {
        console.error('Failed to fetch config:', error)
        // 使用默认值
      } finally {
        setConfigLoading(false)
      }
    }
    fetchConfig()
  }, [])

  useEffect(() => {
    if (configLoading) return
    
    const newSubtotal = getTotalPrice()
    const newShipping = newSubtotal >= config.freeShippingThreshold ? 0 : config.shippingCost
    const newTax = Math.round(newSubtotal * config.taxRate * 100) / 100
    const newTotal = newSubtotal + newShipping + newTax
    
    setSubtotal(newSubtotal)
    setShipping(newShipping)
    setTax(newTax)
    setTotal(newTotal)
    
    // 计算包邮进度
    if (Number.isFinite(config.freeShippingThreshold) && config.freeShippingThreshold > 0 && newSubtotal < config.freeShippingThreshold) {
      setFreeShippingProgress((newSubtotal / config.freeShippingThreshold) * 100)
    } else {
      setFreeShippingProgress(100)
    }
  }, [cartItems, getTotalPrice, config, configLoading])

  const handleCheckout = () => {
    navigate('/checkout')
  }

  const handleRemove = (id) => {
    removeFromCart(id)
  }

  return (
    <div className="cart-page">
      <h1 className="cart-title">Cart</h1>
      
      {cartItems.length === 0 ? (
        <div className="empty-cart">
          <p>Your bag is empty</p>
          <Link to="/products" className="continue-shopping">Continue Shopping</Link>
        </div>
      ) : (
        <div className="cart-container">
          <div className="cart-items-section">
            {cartItems.map(item => (
              <div key={item.id} className="cart-item">
                <img 
                  src={item.images && item.images.length > 0 ? item.images[0] : item.image || ''} 
                  alt={item.name}
                  className="cart-item-image"
                />
                <div className="cart-item-details">
                  <h3 className="cart-item-name">{item.name}</h3>
                  <p className="cart-item-category">{item.category || ''}</p>
                  {item.size && <p className="cart-item-variant">Size: {item.size}</p>}
                  {item.sku && <p className="cart-item-variant">Style: {item.sku}</p>}
                  <p className="cart-item-price">${item.price.toFixed(2)}</p>
                </div>
                <div className="cart-item-actions">
                  <div className="quantity-controls">
                    <button 
                      className="quantity-btn"
                      onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      -
                    </button>
                    <span className="quantity-value">{item.quantity}</span>
                    <button 
                      className="quantity-btn"
                      onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    >
                      +
                    </button>
                  </div>
                  <button 
                    className="remove-btn"
                    onClick={() => handleRemove(item.id)}
                  >
                    Remove
                  </button>
                </div>
                <div className="cart-item-total">
                  ${(item.price * item.quantity).toFixed(2)}
                </div>
              </div>
            ))}
          </div>
          
          <div className="cart-summary-section">
            <h2 className="summary-title">Summary</h2>
            
            <div className="summary-row">
              <span className="summary-label">Subtotal</span>
              <span className="summary-value">${subtotal.toFixed(2)}</span>
            </div>
            
            <div className="summary-row summary-row-shipping">
              <div className="shipping-container">
                <div className="summary-row">
                  <span className="summary-label">Estimated Shipping</span>
                  <span className="summary-value">
                    {shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                {Number.isFinite(config.freeShippingThreshold) && config.freeShippingThreshold > 0 ? (
                  <div className="shipping-note-row">
                    <span className="shipping-note">
                      {shipping === 0 
                        ? `(Order reached $${config.freeShippingThreshold} free shipping threshold)`
                        : `(Free shipping over $${config.freeShippingThreshold}, add $${(config.freeShippingThreshold - subtotal).toFixed(2)} more)`}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
            
            <div className="summary-row">
              <span className="summary-label">Estimated Tax</span>
              <span className="summary-value">${tax.toFixed(2)}</span>
            </div>
            
            {Number.isFinite(config.freeShippingThreshold) && config.freeShippingThreshold > 0 && subtotal < config.freeShippingThreshold && (
              <div className="free-shipping-info">
                <p className="free-shipping-text">
                  Add ${(config.freeShippingThreshold - subtotal).toFixed(2)} more for free shipping!
                </p>
                <div className="free-shipping-progress">
                  <div 
                    className="free-shipping-progress-bar"
                    style={{ width: `${freeShippingProgress}%` }}
                  />
                </div>
              </div>
            )}
            
            {shipping === 0 && (
              <div className="free-shipping-earned">
                <p>You've earned Free Shipping!</p>
              </div>
            )}
            
            <div className="summary-row total-row">
              <span className="summary-label">Total</span>
              <span className="summary-value total-value">${total.toFixed(2)}</span>
            </div>
            
            <button className="checkout-btn" onClick={handleCheckout}>
              Checkout
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Cart
